interface CashLike {
  settled_open_quantity: number
  currency?: string
  sleeve_id?: string
}

/**
 * Sums settled_open_quantity across all cash entries. Safe on legacy
 * single-entry payloads and DoD multi-entry per-sleeve payloads alike.
 */
export function calculateAvailableAccountCash(
  cash: readonly CashLike[] | null | undefined,
): number {
  if (!cash) return 0
  return cash.reduce<number>(
    (sum, cashPosition) => sum + (cashPosition?.settled_open_quantity ?? 0),
    0,
  )
}

/**
 * Distributes `total` across `cash` entries proportional to each entry's
 * current `settled_open_quantity` share. When the existing entries sum to
 * zero (or the array is empty of nonzero entries), falls back to writing
 * the whole amount to the first entry — this preserves legacy single-entry
 * editing semantics for uninitialized cash.
 */
export function distributeCashProportionally<T extends CashLike>(
  cash: readonly T[],
  total: number,
): T[] {
  if (cash.length === 0) return []
  const currentTotal = calculateAvailableAccountCash(cash)
  if (currentTotal === 0) {
    return cash.map((c, i) =>
      i === 0 ? { ...c, settled_open_quantity: total } : c,
    )
  }
  return cash.map((c) => ({
    ...c,
    settled_open_quantity: (c.settled_open_quantity / currentTotal) * total,
  }))
}
